// frontend/src/components/WhatsAppMessageModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-11-24
// Updated:      2026-09-16
//
// Description:
//   WhatsApp-style messenger modal for a Job.
//   • Portalled to document.body with fixed inset-0 overlay (no clipping under JobEditModal).
//   • Header shows contact avatar/initials, contact name (or mobile as fallback), and Job Ref / Job ID.
//   • Chat body renders message bubbles (inbound/outbound) on WhatsApp-style background.
//   • Footer has "type a message" and WhatsApp-green Send pill.
//   • Resolves contact via contact_id -> /api/contacts/{id}, then falls back to props.
//   • ✅ Template sending supported (Twilio Content API) and persists template_preview_text
//     so the UI thread shows the real rendered message, not "(template message)".
//   • ✅ Template variable inputs now support:
//       - light-grey placeholder hints (parsed from variables_schema label e.g. "e.g. John")
//       - safe prefill (forename, job ref) based on variable label (NOT index)
//       - validation based on variable label (no "3rd field is job ref" regressions)
//   • ✅ Chat auto-scroll:
//       - On open / after load: scrolls to most recent message
//       - If focusMessageId is provided: opens scrolled to that message (centered vertically)
//       - If focusMessageId changes while open: scrolls to that message (centered)
//
// Responsive pass (2026-09-16):
//   • Mobile/tablet uses a flat full-height WhatsApp messenger sheet; centred modal starts at lg.
//   • Chat body owns scrolling via min-height:0 rather than forcing modal overflow.
//   • Header metadata wraps safely and the desktop WHATSAPP label is hidden on narrow screens.
//   • Mobile message bubbles use more horizontal space while desktop keeps the existing width.
//   • Selected-template controls are height-constrained and internally scrollable on mobile.
//   • Composer stacks cleanly on phones and returns to the existing horizontal layout at sm+.
//   • No loading, focus scrolling, template validation, conversation or send logic changed.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
import WhatsAppTemplateLibraryModal from "./WhatsAppTemplateLibraryModal.jsx";

const WhatsAppMessageModal = ({
  open,
  onClose,
  jobId,
  jobRef, // Job reference, e.g. "3065"
  job, // optional job object the caller may pass
  contactId, // optional explicit contact id from caller
  contactName, // optional fallback name from caller
  contactPhone, // optional fallback mobile from caller
  contactAvatarUrl, // optional avatar URL from caller

  // NEW (optional):
  // If provided, the modal will open focused on this message (centered vertically).
  // If it changes while the modal is open, it will re-focus.
  focusMessageId = null,
}) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [draft, setDraft] = useState("");

  const [jobMeta, setJobMeta] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);

  const [resolvedContact, setResolvedContact] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);

  const [conversationId, setConversationId] = useState(null);
  const [sending, setSending] = useState(false);

  // Template flow
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // templateVars: { "1": "Leon", "2": "3064", ... }
  const [templateVars, setTemplateVars] = useState({});

  // templateVarMeta: { "1": { label, example, kind }, ... }
  const [templateVarMeta, setTemplateVarMeta] = useState({});

  // templateVarErrors: { "2": "Invalid job ref", ... }
  const [templateVarErrors, setTemplateVarErrors] = useState({});

  // -------------------------------------------------------------
  // Refs for scrolling
  // -------------------------------------------------------------
  const chatScrollRef = useRef(null);
  const messageNodeMapRef = useRef(new Map()); // messageId -> DOM node
  const lastScrollKeyRef = useRef(""); // prevent repeated scroll loops
  const lastMessageCountRef = useRef(0);

  const registerMessageNode = useCallback((id, node) => {
    if (!id) return;
    const map = messageNodeMapRef.current;
    if (node) map.set(String(id), node);
    else map.delete(String(id));
  }, []);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = chatScrollRef.current;
    if (!el) return;
    // scrollHeight can change after paint; keep it robust
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const scrollToMessageCentered = useCallback((messageId, behavior = "auto") => {
    if (!messageId) return false;
    const node = messageNodeMapRef.current.get(String(messageId));
    if (!node) return false;

    // Center message vertically within the scroll container
    const container = chatScrollRef.current;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();

      const currentScrollTop = container.scrollTop;
      const nodeTopInContainer = nodeRect.top - containerRect.top + currentScrollTop;
      const targetTop = nodeTopInContainer - (containerRect.height / 2) + (nodeRect.height / 2);

      container.scrollTo({ top: Math.max(0, targetTop), behavior });
      return true;
    }

    // Fallback
    node.scrollIntoView({ block: "center", behavior });
    return true;
  }, []);

  // -------------------------------------------------------------
  // Helpers: normalize WhatsApp "to" number to +E164 (NO whatsapp: prefix)
  // -------------------------------------------------------------
  const normaliseToE164 = useCallback((raw) => {
    if (!raw) return "";
    let s = String(raw).trim();
    if (!s) return "";

    if (s.toLowerCase().startsWith("whatsapp:")) {
      s = s.split(":", 2)[1]?.trim() || "";
    }

    s = s.replace(/[()\s-]/g, "");

    if (s.startsWith("00")) {
      const digits = s.slice(2).replace(/\D/g, "");
      return digits ? `+${digits}` : "";
    }

    if (s.startsWith("+")) {
      const digits = s.slice(1).replace(/\D/g, "");
      return digits ? `+${digits}` : "";
    }

    const digits = s.replace(/\D/g, "");
    if (!digits) return "";

    // UK-focused normalization (matches backend strategy)
    if (digits.startsWith("44") && digits.length >= 12) return `+${digits}`;
    if (digits.startsWith("0") && digits.length === 11 && digits[1] === "7") return `+44${digits.slice(1)}`;
    if (digits.startsWith("7") && digits.length === 10) return `+44${digits}`;

    // Unknown format: return digits (backend may reject if invalid, but we avoid mangling)
    return digits;
  }, []);

  const buildTemplatePreviewText = useCallback((tpl, vars) => {
    const base =
      (tpl && typeof tpl.body_preview === "string" && tpl.body_preview.trim())
        ? tpl.body_preview.trim()
        : (tpl && typeof tpl.body_text === "string" && tpl.body_text.trim())
          ? tpl.body_text.trim()
          : (tpl && typeof tpl.body === "string" && tpl.body.trim())
            ? tpl.body.trim()
            : "";

    if (!base) return "(template message)";

    let out = base;
    const safeVars = (vars && typeof vars === "object") ? vars : {};

    Object.keys(safeVars).forEach((k) => {
      const val = safeVars[k] == null ? "" : String(safeVars[k]);
      const re = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g");
      out = out.replace(re, val);
    });

    return out;
  }, []);

  // Pull example from label like "Job Ref (e.g 3062)" or "Forename (e.g. John)"
  const extractExampleFromLabel = useCallback((label) => {
    if (!label || typeof label !== "string") return "";
    const s = label.trim();
    // match "(e.g. John)" or "(e.g 3062)" or "e.g. John" with or without parentheses
    const m = s.match(/e\.g\.?\s*([^)]+)/i);
    if (!m) return "";
    let ex = String(m[1] || "").trim();
    ex = ex.replace(/\)\s*$/, "").trim();
    return ex;
  }, []);

  const classifyVarKind = useCallback((label, key) => {
    const l = (label || "").toLowerCase();
    const k = String(key || "").trim();
    if (l.includes("job ref") || l.includes("jobref")) return "job_ref";
    if (l.includes("forename") || l.includes("first name") || l.includes("firstname")) return "forename";
    if (k === "1" && l.includes("customer")) return "forename";
    return "generic";
  }, []);

  // -------------------------------------------------------------
  // Effective contact_id
  // -------------------------------------------------------------
  const effectiveContactId = useMemo(() => {
    if (contactId) return contactId;
    if (job && job.contact_id) return job.contact_id;
    if (jobMeta && jobMeta.contact_id) return jobMeta.contact_id;
    return null;
  }, [contactId, job, jobMeta]);

  // -------------------------------------------------------------
  // Effective customer_id
  // -------------------------------------------------------------
  const effectiveCustomerId = useMemo(() => {
    if (job && job.customer_id) return job.customer_id;
    if (jobMeta && jobMeta.customer_id) return jobMeta.customer_id;
    if (resolvedContact && resolvedContact.customer_id) return resolvedContact.customer_id;
    return null;
  }, [job, jobMeta, resolvedContact]);

  // -------------------------------------------------------------
  // Job meta
  // -------------------------------------------------------------
  useEffect(() => {
    if (!open || !jobId) return;

    let cancelled = false;
    setLoadingJob(true);

    api
      .get(`/api/jobs/${jobId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || null;
        setJobMeta(data);
        console.log("[WhatsAppMessageModal] job meta for job", jobId, data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[WhatsAppMessageModal] failed to load job meta", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingJob(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  // -------------------------------------------------------------
  // Contact resolution
  // -------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (!effectiveContactId) {
      setResolvedContact(null);
      return;
    }

    let cancelled = false;
    setLoadingContact(true);

    api
      .get(`/api/contacts/${effectiveContactId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || null;
        setResolvedContact(data);
        console.log("[WhatsAppMessageModal] resolved contact", effectiveContactId, data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[WhatsAppMessageModal] failed to resolve contact", effectiveContactId, err);
        setResolvedContact(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingContact(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, effectiveContactId]);

  // -------------------------------------------------------------
  // Extract name + phone from resolvedContact
  // -------------------------------------------------------------
  const apiContactName = useMemo(() => {
    if (!resolvedContact) return "";

    const rc = resolvedContact;

    if (typeof rc.display_name === "string" && rc.display_name.trim()) return rc.display_name.trim();
    if (typeof rc.full_name === "string" && rc.full_name.trim()) return rc.full_name.trim();

    const first = typeof rc.first_name === "string" ? rc.first_name.trim() : "";
    const last = typeof rc.last_name === "string" ? rc.last_name.trim() : "";
    if (first || last) return `${first} ${last}`.trim();

    if (typeof rc.name === "string" && rc.name.trim()) return rc.name.trim();

    return "";
  }, [resolvedContact]);

  const apiContactFirstName = useMemo(() => {
    if (!resolvedContact) return "";
    const rc = resolvedContact;
    const first =
      (typeof rc.first_name === "string" && rc.first_name.trim()) ? rc.first_name.trim() :
      (typeof rc.forename === "string" && rc.forename.trim()) ? rc.forename.trim() :
      "";
    if (first) return first;
    const dn = apiContactName || "";
    if (dn.trim()) return dn.trim().split(/\s+/)[0] || "";
    return "";
  }, [resolvedContact, apiContactName]);

  const apiContactPhone = useMemo(() => {
    if (!resolvedContact) return "";
    const rc = resolvedContact;

    const candidates = [
      rc.whatsapp,
      rc.mobile,
      rc.mobile_phone,
      rc.phone_mobile,
      rc.phone,
      rc.phone_number,
      rc.telephone,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
  }, [resolvedContact]);

  // -------------------------------------------------------------
  // Header label logic
  // -------------------------------------------------------------
  const headerPrimaryText = useMemo(() => {
    if (apiContactName) return apiContactName;
    if (contactName && contactName.trim()) return contactName.trim();
    if (apiContactPhone) return apiContactPhone;
    if (contactPhone && contactPhone.trim()) return contactPhone.trim();
    return "Unknown contact";
  }, [apiContactName, apiContactPhone, contactName, contactPhone]);

  const headerSecondaryText = useMemo(() => {
    const phone = apiContactPhone || (contactPhone && contactPhone.trim()) || "";
    if (!phone) return "";
    if (headerPrimaryText !== phone) return phone;
    return "";
  }, [apiContactPhone, contactPhone, headerPrimaryText]);

  const avatarUrl = useMemo(() => {
    if (contactAvatarUrl) return contactAvatarUrl;
    if (resolvedContact && resolvedContact.avatar_url) return resolvedContact.avatar_url;
    return null;
  }, [contactAvatarUrl, resolvedContact]);

  const initials = useMemo(() => {
    if (avatarUrl) return "";

    const basisName = apiContactName || contactName || "";
    const basisPhone = apiContactPhone || contactPhone || "";

    if (basisName.trim()) {
      const parts = basisName.split(" ").filter(Boolean).slice(0, 2);
      return parts.map((p) => p[0].toUpperCase()).join("");
    }

    if (basisPhone.trim()) {
      const digits = basisPhone.replace(/\D/g, "");
      if (digits.length >= 2) return digits.slice(-2);
    }

    return "UC";
  }, [avatarUrl, apiContactName, contactName, apiContactPhone, contactPhone]);

  // -------------------------------------------------------------
  // Job label
  // -------------------------------------------------------------
  const effectiveJobRef = useMemo(() => {
    if (jobRef) return String(jobRef);
    if (job && job.job_ref) return String(job.job_ref);
    if (jobMeta && jobMeta.job_ref) return String(jobMeta.job_ref);
    return "";
  }, [jobRef, job, jobMeta]);

  const effectiveJobId = useMemo(() => {
    if (jobId) return jobId;
    if (job && job.id) return job.id;
    if (jobMeta && jobMeta.id) return jobMeta.id;
    return null;
  }, [jobId, job, jobMeta]);

  const jobLabel = useMemo(() => {
    if (effectiveJobRef && effectiveJobId) return `Job Ref: ${effectiveJobRef} · Job ID: ${effectiveJobId}`;
    if (effectiveJobRef) return `Job Ref: ${effectiveJobRef}`;
    if (effectiveJobId) return `Job ID: ${effectiveJobId}`;
    return "";
  }, [effectiveJobRef, effectiveJobId]);

  // -------------------------------------------------------------
  // Load WhatsApp messages
  // -------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoadingMessages(true);
    setLoadError(null);

    const fetchMessages = async () => {
      try {
        let res;

        if (jobId) {
          res = await api.get(`/api/conversations/job/${jobId}/messages`, { params: { channel: "whatsapp" } });
        } else if (effectiveContactId) {
          res = await api.get(`/api/conversations/contact/${effectiveContactId}/messages`, { params: { channel: "whatsapp" } });
        } else {
          setMessages([]);
          setConversationId(null);
          return;
        }

        if (cancelled) return;

        const rows = Array.isArray(res.data) ? res.data : [];
        let convId = null;

        const normalised = rows.map((m) => {
          if (!convId && m.conversation_id) convId = m.conversation_id;
          return {
            id: m.id || m.message_id || m.conversation_id || Date.now(),
            direction: (m.direction || "outbound").toLowerCase(),
            body: m.body_text || m.body_html || "",
            created_at: m.created_at,
          };
        });

        console.log("[WhatsAppMessageModal] loaded WhatsApp messages", {
          jobId,
          effectiveContactId,
          convId,
          count: normalised.length,
        });

        setMessages(normalised);
        setConversationId(convId);
      } catch (err) {
        if (cancelled) return;
        let detail = "Failed to load WhatsApp messages.";
        if (err.response && err.response.data && err.response.data.detail) detail = String(err.response.data.detail);
        setLoadError(detail);
        setMessages([]);
        setConversationId(null);
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };

    fetchMessages();

    return () => { cancelled = true; };
  }, [open, jobId, effectiveContactId]);

  // -------------------------------------------------------------
  // Auto-scroll behavior
  // -------------------------------------------------------------
  const buildScrollKey = useCallback(() => {
    // Enough uniqueness to detect state changes without constant re-scrolling
    const count = messages.length;
    const lastId = count ? String(messages[count - 1]?.id ?? "") : "";
    const focus = focusMessageId ? String(focusMessageId) : "";
    return `${open ? "1" : "0"}|${loadingMessages ? "1" : "0"}|${count}|${lastId}|${focus}`;
  }, [open, loadingMessages, messages, focusMessageId]);

  useEffect(() => {
    if (!open) return;
    if (loadingMessages) return;

    const key = buildScrollKey();
    if (key === lastScrollKeyRef.current) return;
    lastScrollKeyRef.current = key;

    // Wait for DOM to paint message nodes before measuring
    const raf = requestAnimationFrame(() => {
      // If a focusMessageId exists, try to center it
      if (focusMessageId) {
        const ok = scrollToMessageCentered(focusMessageId, "auto");
        if (ok) return;
      }
      // Otherwise go to latest
      scrollToBottom("auto");
    });

    return () => cancelAnimationFrame(raf);
  }, [
    open,
    loadingMessages,
    messages,
    focusMessageId,
    buildScrollKey,
    scrollToBottom,
    scrollToMessageCentered,
  ]);

  // Keep pinned to bottom when new messages arrive (unless focusing a message)
  useEffect(() => {
    if (!open) return;
    if (loadingMessages) return;

    const prevCount = lastMessageCountRef.current;
    const nextCount = messages.length;
    lastMessageCountRef.current = nextCount;

    if (nextCount <= prevCount) return;
    if (focusMessageId) return; // don't fight the focus view

    // Smooth scroll when user is at/near bottom; otherwise don't yank them
    const el = chatScrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom < 120;

    if (nearBottom) {
      requestAnimationFrame(() => scrollToBottom("smooth"));
    }
  }, [open, loadingMessages, messages, focusMessageId, scrollToBottom]);

  // -------------------------------------------------------------
  // ESC-to-close
  // -------------------------------------------------------------
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (onClose) onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // -------------------------------------------------------------
  // Template selection -> initialize vars + meta + prefills
  // -------------------------------------------------------------
  const buildPrefillForVar = useCallback((kind) => {
    if (kind === "forename") return apiContactFirstName || "";
    if (kind === "job_ref") return effectiveJobRef || "";
    return "";
  }, [apiContactFirstName, effectiveJobRef]);

  const validateVarValue = useCallback((kind, value) => {
    const v = String(value || "").trim();
    if (!v) return "";
    if (kind === "job_ref") {
      if (!/^\d{1,12}$/.test(v)) return "Invalid job ref";
    }
    if (kind === "forename") {
      if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{1,40}$/.test(v)) return "Invalid name";
    }
    return "";
  }, []);

  const handleSelectTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl || null);

    const schema = Array.isArray(tpl?.variables_schema) ? tpl.variables_schema : [];
    const nextVars = {};
    const nextMeta = {};
    const nextErrors = {};

    schema.forEach((v, idx) => {
      const rawKey = (v && (v.key || v.placeholder || v.index)) ?? (idx + 1);
      const k = String(rawKey).replace(/\D/g, "") || String(idx + 1);

      const label = (v && typeof v.label === "string") ? v.label : "";
      const example = extractExampleFromLabel(label);
      const kind = classifyVarKind(label, k);

      const prefill = buildPrefillForVar(kind);
      nextVars[k] = (prefill && String(prefill).trim()) ? String(prefill).trim() : "";

      nextMeta[k] = { label, example, kind };

      const err = validateVarValue(kind, nextVars[k]);
      if (err) nextErrors[k] = err;
    });

    if (Object.keys(nextVars).length === 0) {
      nextVars["1"] = apiContactFirstName || "";
      nextMeta["1"] = { label: "Placeholder 1", example: "", kind: "generic" };
    }

    setTemplateVars(nextVars);
    setTemplateVarMeta(nextMeta);
    setTemplateVarErrors(nextErrors);
  }, [
    apiContactFirstName,
    buildPrefillForVar,
    classifyVarKind,
    extractExampleFromLabel,
    validateVarValue,
  ]);

  const updateTemplateVar = useCallback((k, value) => {
    setTemplateVars((prev) => ({ ...prev, [k]: value }));

    setTemplateVarErrors((prev) => {
      const meta = templateVarMeta[k] || { kind: "generic" };
      const err = validateVarValue(meta.kind, value);

      const next = { ...prev };
      if (err) next[k] = err;
      else delete next[k];
      return next;
    });
  }, [templateVarMeta, validateVarValue]);

  // -------------------------------------------------------------
  // SEND freeform
  // -------------------------------------------------------------
  const handleSendFreeform = async () => {
    const text = draft.trim();
    if (!text) return;
    if (sending) return;

    const targetPhoneRaw = apiContactPhone || (contactPhone && contactPhone.trim()) || "";
    const targetPhone = normaliseToE164(targetPhoneRaw);

    if (!conversationId) {
      console.warn("[WhatsAppMessageModal] No conversationId resolved for job; appending locally only.");
      setMessages((prev) => [...prev, { id: Date.now(), direction: "outbound", body: text, created_at: new Date().toISOString() }]);
      setDraft("");
      return;
    }

    setSending(true);

    const subjectFallback = jobLabel || headerPrimaryText || "WhatsApp message";

    const payload = {
      conversation_id: Number(conversationId),
      channel: "whatsapp",
      subject: subjectFallback,
      body_text: text,
      body_html: null,
      customer_id: effectiveCustomerId,
      metadata_json: targetPhone ? {
        to_whatsapp_normalised: targetPhone,
        to_phone_normalised: targetPhone,
        to_phone_ui: targetPhoneRaw,
      } : {},
    };

    try {
      console.log("[WhatsAppMessageModal] sending WhatsApp message via API", { conversationId, payload });

      const res = await api.post(`/api/conversations/${conversationId}/send`, payload);
      const m = res.data || {};

      const normalised = {
        id: m.id || m.message_id || m.conversation_id || Date.now(),
        direction: (m.direction || "outbound").toLowerCase(),
        body: m.body_text || m.body_html || text,
        created_at: m.created_at || new Date().toISOString(),
      };

      setMessages((prev) => [...prev, normalised]);
      setDraft("");
      setLoadError(null);
    } catch (err) {
      console.error("[WhatsAppMessageModal] failed to send WhatsApp message", err);
      let detail = "Failed to send WhatsApp message.";
      if (err.response && err.response.data && err.response.data.detail) detail = String(err.response.data.detail);
      setLoadError(detail);
    } finally {
      setSending(false);
    }
  };

  // -------------------------------------------------------------
  // SEND template
  // -------------------------------------------------------------
  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;
    if (sending) return;

    if (!conversationId) {
      setLoadError("Cannot send template: conversationId not resolved.");
      return;
    }

    const contentSid = selectedTemplate.content_sid;
    if (!contentSid) {
      setLoadError("Selected template has no content_sid.");
      return;
    }

    const targetPhoneRaw = apiContactPhone || (contactPhone && contactPhone.trim()) || "";
    const targetPhone = normaliseToE164(targetPhoneRaw);

    const previewText = buildTemplatePreviewText(selectedTemplate, templateVars);

    setSending(true);

    const subjectFallback = jobLabel || headerPrimaryText || "WhatsApp message";

    const payload = {
      conversation_id: Number(conversationId),
      channel: "whatsapp",
      subject: subjectFallback,
      body_text: "",
      body_html: "",
      customer_id: effectiveCustomerId,
      metadata_json: {
        template_content_sid: contentSid,
        placeholders: templateVars,
        to_whatsapp_normalised: targetPhone,
        template_preview_text: previewText,
      },
    };

    try {
      console.log("[WhatsAppMessageModal] sending WhatsApp TEMPLATE via API", { conversationId, payload });

      const res = await api.post(`/api/conversations/${conversationId}/send`, payload);
      const m = res.data || {};

      const normalised = {
        id: m.id || m.message_id || m.conversation_id || Date.now(),
        direction: (m.direction || "outbound").toLowerCase(),
        body: m.body_text || previewText,
        created_at: m.created_at || new Date().toISOString(),
      };

      setMessages((prev) => [...prev, normalised]);

      setSelectedTemplate(null);
      setTemplateVars({});
      setTemplateVarMeta({});
      setTemplateVarErrors({});
      setLoadError(null);
    } catch (err) {
      console.error("[WhatsAppMessageModal] failed to send WhatsApp template", err);
      let detail = "Failed to send WhatsApp template.";
      if (err.response && err.response.data && err.response.data.detail) detail = String(err.response.data.detail);
      setLoadError(detail);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return createPortal(
    <>
      {/* ✅ HARD GUARANTEE: Put template modal inside a top-level fixed stacking root ABOVE WhatsApp overlay. */}
      {templateLibraryOpen &&
        createPortal(
          <div
            className="fixed inset-0"
            style={{
              zIndex: 250000, // higher than WhatsApp overlay (150000)
              pointerEvents: "auto",
            }}
          >
            <WhatsAppTemplateLibraryModal
              isOpen={templateLibraryOpen}
              onClose={() => setTemplateLibraryOpen(false)}
              onSelectTemplate={(tpl) => handleSelectTemplate(tpl)}
            />
          </div>,
          document.body
        )}

      <div
        className="fixed inset-0 z-[150000] flex items-stretch justify-center bg-black/60 p-0 lg:items-center lg:p-4"
        onMouseDown={templateLibraryOpen ? undefined : onClose}
        style={{
          // While template modal is open, WhatsApp overlay cannot steal clicks / focus
          pointerEvents: templateLibraryOpen ? "none" : "auto",
        }}
      >
        <div
          className="flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden rounded-none bg-white shadow-none lg:h-auto lg:max-h-[80vh] lg:max-w-2xl lg:rounded-3xl lg:shadow-2xl"
          onMouseDown={(e) => e.stopPropagation()}
          style={{ pointerEvents: "auto" }}
        >
          {/* HEADER */}
          <div className="relative flex min-w-0 shrink-0 items-center bg-[#075E54] px-3 py-3 pr-12 text-white lg:px-5 lg:py-4 lg:pr-14">
            <div className="mr-3 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#128C7E] text-sm font-semibold">
              {avatarUrl ? (
                <img src={avatarUrl} alt={headerPrimaryText} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col">
              <span className="break-words font-semibold leading-tight">{headerPrimaryText}</span>

              {(headerSecondaryText || loadingContact) && (
                <span className="mt-0.5 break-all text-[11px] opacity-80">
                  {loadingContact ? "Resolving contact…" : headerSecondaryText}
                </span>
              )}

              {jobLabel && <span className="mt-0.5 break-words text-[11px] opacity-80">{jobLabel}</span>}
            </div>

            <div className="ml-2 hidden shrink-0 text-xs font-semibold tracking-widest lg:block">WHATSAPP</div>

            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/20 text-base leading-none text-white hover:bg-black/35 active:bg-black/45 lg:right-4 lg:top-4 lg:h-7 lg:w-7"
              title="Close"
            >
              ×
            </button>
          </div>

          {/* CHAT BODY */}
          <div
            ref={chatScrollRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[#e5ddd5] px-3 py-3 lg:px-4"
          >
            {(loadingMessages || loadingJob) && (
              <div className="text-center text-[11px] text-gray-700 opacity-70 mt-2">
                Loading WhatsApp history…
              </div>
            )}

            {loadError && !loadingMessages && (
              <div className="text-center text-[11px] text-red-700 opacity-80 mt-2">{loadError}</div>
            )}

            {!loadingMessages && !loadError && messages.length === 0 && (
              <div className="text-center text-[11px] text-gray-700 opacity-70 mt-2">
                WhatsApp history will appear here once messages exist for this job/contact.
              </div>
            )}

            {messages.map((m) => {
              const isOutbound = m.direction === "outbound";
              return (
                <div
                  key={m.id}
                  ref={(node) => registerMessageNode(m.id, node)}
                  className={`w-full flex mb-1 ${isOutbound ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`relative max-w-[86%] rounded-lg px-3 py-2 text-sm lg:max-w-[70%] ${isOutbound ? "bg-[#dcf8c6]" : "bg-white"}`}
                  >
                    <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{m.body}</div>
                    {m.created_at && (
                      <div className="text-[10px] text-gray-500 text-right mt-1">
                        {new Date(m.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* TEMPLATE PANEL (only when a template is selected) */}
          {selectedTemplate && (
            <div className="max-h-[42dvh] shrink-0 overflow-y-auto border-t bg-white px-3 py-3 lg:max-h-none lg:overflow-visible lg:px-4">
              <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center">
                <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                  <div className="shrink-0 text-sm font-semibold" style={{ color: "#075E54" }}>Template:</div>
                  <div className="min-w-0 break-words text-sm font-semibold">{selectedTemplate.name}</div>
                </div>
                <div className="flex w-full gap-2 lg:ml-auto lg:w-auto lg:items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTemplate(null);
                      setTemplateVars({});
                      setTemplateVarMeta({});
                      setTemplateVarErrors({});
                    }}
                    className="flex-1 bg-btnGeneralNormal text-xs text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick lg:flex-none"
                    style={{ padding: "6px 10px", borderRadius: 999 }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleSendTemplate}
                    disabled={sending}
                    className="flex-1 bg-btnActionNormal text-xs text-white hover:bg-btnActionHover active:bg-btnActionClick lg:flex-none"
                    style={{ padding: "6px 12px", borderRadius: 999, opacity: sending ? 0.7 : 1 }}
                    title={Object.keys(templateVarErrors).length ? "Fix validation issues before sending" : "Send template"}
                  >
                    {sending ? "Sending…" : "Send Template"}
                  </button>
                </div>
              </div>

              <div className="mt-3 grid min-w-0 grid-cols-1 gap-2 md:grid-cols-2">
                {Object.keys(templateVars).sort((a, b) => Number(a) - Number(b)).map((k) => {
                  const meta = templateVarMeta[k] || {};
                  const hint = meta.example || "";
                  const err = templateVarErrors[k] || "";
                  const isError = !!err;

                  return (
                    <div key={k}>
                      <label className="block text-[11px] text-gray-600 mb-1">
                        {meta.label && meta.label.trim() ? meta.label : `Placeholder ${k}`}
                      </label>

                      <input
                        type="text"
                        className="w-full min-w-0 rounded-lg border bg-white px-3 py-2 text-sm outline-none"
                        style={{
                          borderColor: isError ? "#c0392b" : "#d1d5db",
                        }}
                        value={templateVars[k] || ""}
                        onChange={(e) => updateTemplateVar(k, e.target.value)}
                        placeholder={hint ? hint : "Enter value…"}
                      />

                      {isError && (
                        <div className="text-[11px] mt-1" style={{ color: "#c0392b" }}>
                          {err}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                <div className="text-[11px] text-gray-600 mb-1">Preview</div>
                <div className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm [overflow-wrap:anywhere]">
                  {buildTemplatePreviewText(selectedTemplate, templateVars)}
                </div>
              </div>
            </div>
          )}

          {/* INPUT BAR */}
          <div className="flex shrink-0 flex-col gap-2 border-t bg-[#f0f0f0] px-3 py-3 lg:flex-row lg:items-center lg:gap-3 lg:px-4">
            <button
              type="button"
              onClick={() => setTemplateLibraryOpen(true)}
              className="w-full bg-btnGeneralNormal text-sm text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick lg:w-auto"
              style={{ padding: "8px 12px", borderRadius: 999 }}
              title="Choose a WhatsApp template"
            >
              Template
            </button>

            <div className="min-w-0 flex-1">
              <input
                type="text"
                className="w-full min-w-0 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm outline-none focus:border-[#128C7E]"
                placeholder="Type a message"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFreeform();
                  }
                }}
                disabled={!!selectedTemplate}
              />
            </div>

            <button
              type="button"
              onClick={handleSendFreeform}
              disabled={sending || !!selectedTemplate}
              className="w-full min-w-[72px] rounded-full bg-[#25D366] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1ebe5a] active:bg-[#1aaf52] lg:w-auto"
              style={{ opacity: sending || selectedTemplate ? 0.7 : 1 }}
              title={selectedTemplate ? "Clear template to send freeform" : "Send freeform message"}
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default WhatsAppMessageModal;
